import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../lib/format';
import type { Auction } from '../types';

const quickIncrements = [1, 5, 10, 50];

export function BidPanel({ auction, onBid }: { auction: Auction; onBid?: () => void }) {
  const { user } = useAuth();
  const [customAmount, setCustomAmount] = useState('');
  const [autoBidEnabled, setAutoBidEnabled] = useState(false);
  const [autoBidMax, setAutoBidMax] = useState('');
  const [busy, setBusy] = useState(false);
  const [savingAutoBid, setSavingAutoBid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoBidMessage, setAutoBidMessage] = useState<string | null>(null);

  const minimum = useMemo(() => auction.bid_count === 0
    ? Math.ceil(Number(auction.starting_price))
    : Math.ceil(Number(auction.current_amount) + Number(auction.min_increment)), [auction]);

  useEffect(() => {
    supabase.from('auction_user_preferences').select('auto_bid_enabled,auto_bid_max').eq('auction_id', auction.id).maybeSingle()
      .then(({ data }) => {
        setAutoBidEnabled(Boolean(data?.auto_bid_enabled));
        setAutoBidMax(data?.auto_bid_max == null ? '' : String(Math.trunc(Number(data.auto_bid_max))));
      });
  }, [auction.id, auction.current_amount]);

  const placeBid = async (amount: number) => {
    if (!Number.isInteger(amount)) return setError('Inserisci un importo in euro interi, senza decimali.');
    if (amount < minimum) return setError(`Offerta minima: ${minimum} €`);
    setBusy(true); setError(null);
    try {
      const { error: rpcError } = await supabase.rpc('place_bid', { p_auction_id: auction.id, p_amount: amount });
      if (rpcError) throw rpcError;
      setCustomAmount(''); onBid?.();
    } catch (err) { setError(err instanceof Error ? err.message : 'Errore durante il rilancio'); }
    finally { setBusy(false); }
  };

  const saveAutoBid = async () => {
    const max = Number(autoBidMax);
    if (autoBidEnabled && (!Number.isInteger(max) || max < minimum)) {
      setError(`Inserisci un massimo in euro interi, almeno ${minimum} €.`); return;
    }
    setSavingAutoBid(true); setError(null); setAutoBidMessage(null);
    try {
      const { error: rpcError } = await supabase.rpc('set_auction_preferences', {
        p_auction_id: auction.id, p_notifications_enabled: null,
        p_auto_bid_enabled: autoBidEnabled, p_auto_bid_max: autoBidMax ? max : null
      });
      if (rpcError) throw rpcError;
      setAutoBidMessage(autoBidEnabled ? `Rilancio automatico attivo fino a ${max} €. L’offerta viene aggiornata subito se necessario.` : 'Rilancio automatico disattivato.');
      onBid?.();
    } catch (err) { setError(err instanceof Error ? err.message : 'Impossibile salvare il rilancio automatico.'); }
    finally { setSavingAutoBid(false); }
  };

  const isClosed = auction.status !== 'open' || new Date(auction.closes_at).getTime() <= Date.now();
  const isCreator = user?.id === auction.created_by;

  if (isCreator) {
    return <section className="panel bid-panel">
      <div className="panel-header"><div><span className="eyebrow">Offerte</span><h2>Non puoi partecipare</h2></div><div className="current-price">{auction.bid_count ? formatCurrency(auction.current_amount) : 'Nessuna offerta'}</div></div>
      <p className="muted">Hai creato questa asta: il creatore non può fare offerte né attivare il rilancio automatico sul proprio giocatore.</p>
    </section>;
  }

  return <section className="panel bid-panel">
    <div className="panel-header"><div><span className="eyebrow">Offerta minima</span><h2>{formatCurrency(minimum)}</h2></div><div className="current-price">{auction.bid_count ? formatCurrency(auction.current_amount) : 'Nessuna offerta'}</div></div>

    {auction.bid_count === 0 ? <button className="primary-button first-bid-button" disabled={busy || isClosed} onClick={() => placeBid(minimum)}>Offri {formatCurrency(minimum)}</button> : <div className="quick-bids">{quickIncrements.map((increment) => <button key={increment} className="primary-button soft" disabled={busy || isClosed} onClick={() => placeBid(Math.ceil(Number(auction.current_amount)) + increment)}>+{increment} €</button>)}</div>}

    <form className="custom-bid" onSubmit={(event) => { event.preventDefault(); const amount = Number(customAmount); if (Number.isFinite(amount)) placeBid(amount); }}>
      <input inputMode="numeric" pattern="[0-9]*" step="1" min={minimum} placeholder={`Importo libero min. ${minimum} €`} value={customAmount} disabled={busy || isClosed} onChange={(event) => setCustomAmount(event.target.value.replace(/[^0-9]/g, ''))} />
      <button className="primary-button" disabled={busy || isClosed}>Offri</button>
    </form>

    <div className="auto-bid-box">
      <label className="setting-row auto-bid-toggle"><span><strong>Rilancio automatico</strong><small>Partecipa automaticamente anche se non hai ancora offerto e ti mantiene in vantaggio fino al limite scelto.</small></span><input type="checkbox" checked={autoBidEnabled} disabled={isClosed} onChange={(e) => setAutoBidEnabled(e.target.checked)} /></label>
      {autoBidEnabled && <label>Massimo automatico (€)<input inputMode="numeric" pattern="[0-9]*" value={autoBidMax} disabled={isClosed} placeholder={`Almeno ${minimum}`} onChange={(e) => setAutoBidMax(e.target.value.replace(/[^0-9]/g, ''))} /></label>}
      <button type="button" className="ghost-button" disabled={savingAutoBid || isClosed} onClick={saveAutoBid}>{savingAutoBid ? 'Salvataggio…' : 'Salva impostazione automatica'}</button>
      {autoBidMessage && <p className="success-text">{autoBidMessage}</p>}
    </div>
    {isClosed && <p className="muted">Asta chiusa: non è più possibile rilanciare.</p>}
    {error && <p className="error-text">{error}</p>}
  </section>;
}
